import { useEffect, useId, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import BunkerBargeIMOField from "@features/vessel-call/vessel-call-status/PortServices/form/bunker-barge/BunkerBargeIMOField";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useOffshoreServiceCompanies } from "@hooks/useOffshoreServiceCompanies";
import { getAnchorageAreas, getBerths, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type FromToLocation, type UnitsOfMeasurement } from "@services/vessel-call/types";
import { useQuery } from "@tanstack/react-query";
import {
  clearFieldNavigationTiming,
  startFieldNavigationTiming,
} from "@telemetry/fieldNavigationTiming";
import { useTrackPendingFieldNavigation } from "@telemetry/trackPendingFieldNavigation";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type BunkeringFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  currentVesselImoNumber?: string;
  selectedPortCode?: string;
};

const BunkeringFields = ({ langBase }: { langBase: string }) => {
  const { formData, updateFormData, currentVesselImoNumber, selectedPortCode } =
    useOutletContext<BunkeringFieldsOutlet>();

  const { t } = useTranslation();
  const { options: locations } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );
  const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode } = usePortServicesFormStore();
  const expectedLocation = formData.watch("bunkeringType");
  const fieldNavigationId = useId();
  const locationTimingId = `${fieldNavigationId}:${expectedLocation}`;
  const selectedLocation = locations.find((item) => item.fromToLocationCode === expectedLocation);
  const isBerth = selectedLocation?.location === "BERTH";
  const isAnchorage = selectedLocation?.location === "ANCH";
  const berthsQuery = useQuery({
    queryKey: ["berths", expectedLocation],
    queryFn: () => getBerths("bff-vessel-call", expectedLocation!),
    enabled: !!expectedLocation && isBerth,
  });
  const anchorageQuery = useQuery({
    queryKey: ["anchorageAreas", expectedLocation],
    queryFn: () => getAnchorageAreas("bff-vessel-call", expectedLocation!),
    enabled: !!expectedLocation && isAnchorage,
  });
  const { data: berths = [], isFetching: isBerthsLoading } = berthsQuery;
  const { data: anchorageAreas = [], isFetching: isAnchorageLoading } = anchorageQuery;
  useTrackPendingFieldNavigation(locationTimingId, isBerth ? berthsQuery : anchorageQuery);
  useEffect(() => () => clearFieldNavigationTiming(locationTimingId), [locationTimingId]);
  const areaOptions = useMemo(() => {
    const options = isBerth
      ? berths.map(({ berthCode, berthName }) => ({ value: berthCode, label: berthName }))
      : isAnchorage
        ? anchorageAreas.map((area) => ({
            value: area.anchorageAreaCode,
            label: area.anchorageAreaDesc,
          }))
        : [];
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [isBerth, isAnchorage, berths, anchorageAreas]);

  const handleExpectedLocationChange = (key: string, value: unknown) => {
    updateFormData(key, value);
    if (value !== expectedLocation) {
      clearFieldNavigationTiming(locationTimingId);
      const nextLocation = locations.find((item) => item.fromToLocationCode === value);
      if (nextLocation?.location === "BERTH" || nextLocation?.location === "ANCH") {
        startFieldNavigationTiming(`${fieldNavigationId}:${value}`, {
          module: "vessel_call",
          screen: "Add Port Service",
          action: "load_bunkering_locations",
          fromField: "bunkeringType",
          toField: "berthOrAnchorageCode",
          entityType: "vessel_call",
        });
      }
      formData.setValue("berthOrAnchorageCode", "", { shouldDirty: true });
      updateFormData("berthOrAnchorageCode", "");
    }
  };

  const { options: offshoreCompanies = [] } = useOffshoreServiceCompanies(
    {
      portServiceCategoryCode: serviceCategoryCode,
      portServiceTypeCode: serviceTypeCode,
      portServiceSubtypeCode: serviceSubTypeCode,
    },
    "bff-vessel",
  );
  const { options: unitsOfMeasurement } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-vessel-call",
  );

  const companyOptions = useMemo(
    () =>
      offshoreCompanies
        .map(({ companyNameEnglish, companyCode }) => companyNameEnglish ?? companyCode ?? "")
        .filter((companyName) => companyName.trim().length > 0)
        .map((companyName) => ({
          label: companyName,
          value: companyName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [offshoreCompanies],
  );

  const unitOfMeasurementOptions = useMemo(
    () =>
      unitsOfMeasurement
        .filter((u) => u.isCargoUnit)
        .map((u) => ({ label: u.unitDescription, value: u.unitDescription }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [unitsOfMeasurement],
  );

  const locationOptions = useMemo(
    () =>
      locations
        .filter(
          ({ portCode }) =>
            portCode === null || portCode?.toLowerCase() === selectedPortCode?.toLowerCase(),
        )
        .map(({ locationName, fromToLocationCode }) => ({
          label: locationName,
          value: fromToLocationCode,
        }))
        .filter((location) => location.label !== "HIGH SEAS")
        .sort((a, b) => a.label.localeCompare(b.label)),
    [locations, selectedPortCode],
  );

  return (
    <FormProvider {...formData}>
      <div className="grid grid-cols-4 gap-2">
        <div className="order-1 col-span-2">
          <FormItem<PortServicesFormData, "bunkeringType">
            name="bunkeringType"
            type="autocomplete"
            options={locationOptions}
            label={t(`${langBase}.fields.expected-bunkering-location`)}
            testId="bunkering-type"
            onFieldChange={handleExpectedLocationChange}
          />
        </div>
        <div className="order-1 col-span-2">
          <FormItem<PortServicesFormData, "berthOrAnchorageCode">
            name="berthOrAnchorageCode"
            type="autocomplete"
            options={areaOptions}
            label={t(`${langBase}.fields.berth-anchorage-area-location`)}
            testId="bunkering-location"
            disabled={
              !expectedLocation ||
              (!isBerth && !isAnchorage) ||
              isBerthsLoading ||
              isAnchorageLoading
            }
            onFieldChange={updateFormData}
          />
        </div>

        <div
          data-testid="bunkering-bunker-barge-layout"
          className="contents [&>*:first-child]:order-2 [&>*:last-child]:order-4 [&>*:last-child]:col-start-1"
        >
          <BunkerBargeIMOField
            formData={formData}
            updateFormData={updateFormData}
            langBase={langBase}
            currentVesselImoNumber={currentVesselImoNumber}
            selectedPortCode={selectedPortCode}
          />
        </div>
        <div className="order-3 col-span-2">
          <FormItem<PortServicesFormData, "bunkeringCompanyName">
            testId="bunkeringCompanyName"
            name="bunkeringCompanyName"
            type="autocomplete"
            options={companyOptions}
            label={t(`${langBase}.fields.bunkering-company-name`)}
            onFieldChange={updateFormData}
            enforceUppercase
          />
        </div>
        <div className="order-5 col-span-1 col-start-1">
          <FormItem<PortServicesFormData, "quantity">
            name="quantity"
            type="number"
            label={t(`${langBase}.fields.quantity`)}
            testId="quantity"
            onFieldChange={updateFormData}
            maxDigits={15}
            canBeginWithZero={false}
          />
        </div>
        <div className="order-6 col-span-1">
          <FormItem<PortServicesFormData, "unitOfMeasurement">
            name="unitOfMeasurement"
            type="autocomplete"
            options={unitOfMeasurementOptions}
            label={t(`${langBase}.fields.unit-of-measurement`)}
            testId="unit-of-measurement"
            onFieldChange={updateFormData}
          />
        </div>

        <div className="order-7 col-span-2 col-start-1">
          <FormItem<PortServicesFormData, "rts">
            name="rts"
            type="date-picker"
            label={t(`${langBase}.fields.rts`)}
            testId="portServices-rts"
            onFieldChange={updateFormData}
            timePicker
            combinedInput
            dateConstraint="not-past"
            minDate={new Date()}
            maxDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default BunkeringFields;
